import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { c } from 'ttag';

import { useConsent } from '@freelancelabs/components';
import { LogoFreelance } from '@freelancelabs/icons';

import { PublicRoutes } from '../../constants';
import LanguageSwitcher from '../components/LanguageSwitcher';
import LoginAnimation from './LoginAnimation';
import LoginClaim from './LoginClaim';

interface Props {
    children: ReactNode;
}

/**
 * La composition des écrans publics.
 *
 * **Deux changements de la charte V3, et rien de plus.**
 *
 * L'aparté de droite : l'image du bateau, qui était un décor, laisse la place à
 * l'animation, qui montre ce que le portail fait.
 *
 * Le logo passe en **signature d'angle**, en haut à gauche, là où il était centré
 * au-dessus du formulaire. C'est ce qui laisse le formulaire aligné à gauche sur ses
 * libellés : un logo centré au-dessus d'un formulaire aligné à gauche donne deux axes
 * de lecture. Sa taille, 104 px, est celle de la maquette, ajoutée à l'échelle du
 * design system dans ce lot.
 *
 * L'habillage vient des jetons : ni classe ni feuille de style propres à cet écran.
 */
const PublicLayout = ({ children }: Props) => {
    const { openConsent } = useConsent();
    return (
        <div className="public-layout h-full min-w-full flex-nowrap grid md:grid-cols-2">
            {/* `relative` ancre le logo, que la charte V3 pose en signature
                d'angle (`#l-logo{position:absolute;top:26px;left:32px}`). */}
            <div className="relative flex flex-col px-4 md:px-6">
                <header className="container flex justify-end items-center h-16">
                    <span className="absolute top-[26px] left-[32px]">
                        <LogoFreelance size={104} />
                    </span>
                    {/*
                        LE COMMUTATEUR DE LANGUE VIT DANS L'APARTE, EN HAUT A DROITE, comme
                        Cloudflare le pose au coin haut-droit de sa page. Celui-ci est le
                        MEME, garde pour les petits ecrans et lui seul.

                        Attention, CE N'EST PAS UN DOUBLON DE CONFORT : `.public-layout aside`
                        passe en `display: none` en dessous de 900 px, donc un commutateur
                        pose la-bas seulement DISPARAITRAIT sur telephone, et la langue ne
                        pourrait plus se changer du tout. Les deux ne sont jamais visibles
                        ensemble, chacun sur le fond auquel son habillage repond.
                    */}
                    <span className="md:hidden">
                        <LanguageSwitcher />
                    </span>
                </header>
                <main className="flex flex-1 md:items-center justify-center py-6">
                    <section className="md:max-w-md flex-1 flex flex-col md:rounded-2xl">{children}</section>
                </main>
                {/* `gap-[18px]` : l'ecart de la maquette entre les deux mentions
                    (`.l-footer{gap:18px}`). Il etait porte par un `ml-2` sur le
                    second, soit 8 px, et seulement d'un cote. */}
                <div className="h-16 flex items-center justify-center gap-[18px]">
                    {/* `link-weak` : le registre gris du pied de page, declare une
                        fois dans le design system. La taille et la graisse viennent
                        de la variante, plus d'utilitaires poses ici. */}
                    <Link className="link link-weak" to={PublicRoutes.CGU}>
                        {c('Link').t`Legal notice`}
                    </Link>
                    {openConsent && (
                        <span className="link link-weak cursor-pointer" onClick={openConsent}>
                            {c('Link').t`Cookies parameters`}
                        </span>
                    )}
                </div>
            </div>
            {/* L'aparté garde sa classe et sa place ; seul son contenu change, et son
                aplat bleu vient du design system. */}
            <aside className="relative hidden md:flex items-center justify-center overflow-hidden">
                {/*
                    En vis-a-vis exact du logo, qui est pose a `top:26px; left:32px` par la
                    charte V3 : le commutateur prend les memes 26 et 32 de l'autre bord, donc
                    les deux signatures d'angle sont sur la meme ligne d'horizon.

                    `inverse` parce que l'aplat est `--portal-accent` : voir la variante.
                */}
                <span className="absolute top-[26px] right-[32px] z-10">
                    <LanguageSwitcher category="inverse" />
                </span>
                {/* La COLONNE, et non l'aparte, porte la largeur. `items-center` la centre
                    et l'animation y garde sa taille native : etiree sur toute la largeur
                    de l'aparte elle passait de 560 a 800 px, mesure a l'ecran. Le texte
                    prend la meme mesure, ce qui l'aligne sur le bord gauche du dessin au
                    lieu de flotter en dessous. */}
                <div className="flex w-[560px] max-w-full flex-col">
                    <LoginAnimation />
                    <LoginClaim />
                </div>
            </aside>
        </div>
    );
};

export default PublicLayout;
